import DownloadIcon from '@mui/icons-material/Download';
import ListAltIcon from '@mui/icons-material/ListAlt';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  FormHelperText,
  IconButton,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { BULK_SEND_LABELS, SIM_CHANGE_PROCESS_TYPES } from '@app/shared';
import type { BulkBatchDto, BulkOperation, UnitStatus } from '@app/shared';
import { AgentChainSelect, deepestAgent } from '../../components/AgentChainSelect';
import { ConfirmDialog } from '../../components/dialogs';
import type { AgentChain } from '../../components/AgentChainSelect';
import { DataTable, rowNumber } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { ApiError } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { useAuth } from '../auth/AuthProvider';
import { RequirePermission } from '../auth/guards';
import { useLookupOptions } from '../configuration/api';
import { useCarriers } from '../plans/lookups';
import { BatchRowsDialog } from './BatchRowsDialog';
import {
  addUnitsTemplateUrl,
  useBulkBatches,
  useBulkSend,
  useBulkSubmit,
  useChangeReasonOptions,
} from './api';
import type { BulkSubmit } from './api';
import { MAX_BULK_VALUES, bulkConfigFor, parseIdentifiers } from './bulkConfig';
import type { BulkConfig, BulkExtra } from './bulkConfig';
import { REASON_REQUIRED_STATUSES } from './labels';

type InputType = 'paste' | 'file';
/** Keyed by the field the API names: "identifiers", "file", "ownerUserId", a parameter... */
type Problems = Record<string, string | undefined>;

/** Keeps the native file input in the tab order while only the button is visible. */
const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
} as const;

const summary = (b: BulkBatchDto) =>
  b.sendLabel
    ? `${b.name}: ${b.total} record(s) uploaded. Press "${b.sendLabel}" in the report below to run it.`
    : `${b.name}: ${b.total} record(s), ${b.success} success, ${b.failure} failure.`;

const severityOf = (b: BulkBatchDto) => {
  if (b.sendLabel) return 'info' as const;
  if (b.failure === 0) return 'success' as const;
  return b.success === 0 ? ('error' as const) : ('warning' as const);
};

const sampleHref = (header: string) =>
  `data:text/csv;charset=utf-8,${encodeURIComponent(`${header}\n`)}`;

/** Batch report of one operation, with the details action and the second step of two-step batches. */
function BatchReport({
  config,
  onDetails,
  onSent,
}: {
  config: BulkConfig;
  onDetails: (batch: BulkBatchDto) => void;
  onSent: (batch: BulkBatchDto) => void;
}) {
  const { can } = useAuth();
  const notify = useNotify();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const batches = useBulkBatches(config.operation, { page, pageSize });
  const send = useBulkSend();
  const headers = config.reportHeaders;
  const twoStep = Boolean(BULK_SEND_LABELS[config.operation]);

  const run = async (batch: BulkBatchDto) => {
    try {
      const done = await send.mutateAsync(batch.id);
      onSent(done);
      notify.success(summary(done));
    } catch (err) {
      notify.error(err);
    }
  };

  const columns: Column<BulkBatchDto>[] = [
    { key: '#', header: '#', width: 50, render: (_b, i) => rowNumber(i, page, pageSize) },
    { key: 'name', header: 'Batch Name', nowrap: true, render: (b) => b.name },
    ...(headers.fileName
      ? [
          {
            key: 'fileName',
            header: headers.fileName,
            render: (b: BulkBatchDto) => b.fileName ?? '',
          },
        ]
      : []),
    {
      key: 'uploadedAt',
      header: headers.uploadedAt,
      nowrap: true,
      render: (b) => formatDateTime(b.uploadedAt),
    },
    { key: 'uploadedBy', header: 'Uploaded By', render: (b) => b.uploadedBy?.loginId ?? '' },
    { key: 'total', header: headers.total, align: 'right', render: (b) => b.total },
    { key: 'success', header: headers.success, align: 'right', render: (b) => b.success },
    { key: 'failure', header: headers.failure, align: 'right', render: (b) => b.failure },
    ...(twoStep
      ? [
          {
            key: 'state',
            header: 'Status',
            nowrap: true,
            render: (b: BulkBatchDto) =>
              b.sendLabel && can(config.code) ? (
                <Button
                  size="small"
                  variant="contained"
                  disabled={send.isPending}
                  onClick={() => void run(b)}
                  aria-label={`${b.sendLabel} ${b.name}`}
                >
                  {b.sendLabel}
                </Button>
              ) : (
                b.state
              ),
          },
        ]
      : []),
    {
      key: 'details',
      header: 'Details',
      render: (b) => (
        <Tooltip title="View details">
          <IconButton size="small" onClick={() => onDetails(b)} aria-label={`Details of ${b.name}`}>
            <ListAltIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box component="section" aria-label={config.reportTitle} sx={{ mt: 3 }}>
      <Typography variant="h3" sx={{ mb: 1 }}>
        {config.reportTitle}
      </Typography>
      <DataTable
        label={config.reportTitle}
        columns={columns}
        rows={batches.data?.data}
        rowKey={(b) => b.id}
        loading={batches.isFetching}
        error={batches.error}
        onRetry={() => void batches.refetch()}
        emptyText="No batches have been uploaded yet."
        page={page}
        pageSize={pageSize}
        total={batches.data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        pageSizeOptions={[10, 20, 50, 100]}
      />
    </Box>
  );
}

/** One field of `config.extras`: a text box or a select filled from a list. */
function ExtraField({
  extra,
  value,
  problem,
  onChange,
}: {
  extra: BulkExtra;
  value: string;
  problem: string | undefined;
  onChange: (value: string) => void;
}) {
  const disconnectReasons = useLookupOptions(
    'disconnectReason',
    extra.source === 'disconnectReason',
  );
  const changeReasons = useChangeReasonOptions(extra.source === 'changeReason');
  const carriers = useCarriers(extra.source === 'carrier');

  if (extra.source === 'text')
    return (
      <TextField
        required={extra.required}
        label={extra.label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        error={Boolean(problem)}
        helperText={problem ?? extra.help}
        inputProps={{ maxLength: 250 }}
        sx={{ gridColumn: { md: 'span 2' } }}
      />
    );

  let options: Array<{ value: string; label: string }> = [];
  let failed = false;
  if (extra.source === 'options')
    options = (extra.options ?? []).map((o) => ({ value: o, label: o }));
  else if (extra.source === 'disconnectReason') {
    options = (disconnectReasons.data ?? []).map((r) => ({ value: r.id, label: r.name }));
    failed = disconnectReasons.isError;
  } else if (extra.source === 'changeReason') {
    // Bulk Swap SIM is an ESN/SIM change, so only the reasons of that process are offered.
    options = (changeReasons.data ?? [])
      .filter((r) => (SIM_CHANGE_PROCESS_TYPES as readonly string[]).includes(r.processType))
      .map((r) => ({ value: r.id, label: r.name }));
    failed = changeReasons.isError;
  } else {
    options = (carriers.data ?? []).map((c) => ({ value: c.id, label: c.name }));
    failed = carriers.isError;
  }

  return (
    <TextField
      select
      required={extra.required}
      label={extra.label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      error={Boolean(problem)}
      helperText={problem ?? (failed ? `Could not load ${extra.label}` : extra.help)}
    >
      {extra.required || extra.initial ? null : (
        <MenuItem value="">
          <em>{extra.source === 'carrier' ? 'All' : '--Select--'}</em>
        </MenuItem>
      )}
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** The submit form: parameters, then "Type - 1" (paste) or "Type - 2" (CSV file). */
function BulkForm({
  config,
  lastBatch,
  onBatch,
  onDetails,
}: {
  config: BulkConfig;
  lastBatch: BulkBatchDto | null;
  onBatch: (batch: BulkBatchDto) => void;
  onDetails: (batch: BulkBatchDto) => void;
}) {
  const notify = useNotify();
  const submitBulk = useBulkSubmit(config.operation);
  const reasons = useChangeReasonOptions(Boolean(config.status) && !config.agentOrStatus);

  const [inputType, setInputType] = useState<InputType>(config.paste ? 'paste' : 'file');
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  // Remounts the file input after a submit; a file input cannot be cleared through its value.
  const [fileKey, setFileKey] = useState(0);
  const [chain, setChain] = useState<AgentChain>({});
  const [status, setStatus] = useState<UnitStatus | ''>('');
  const [reasonId, setReasonId] = useState('');
  const [extraValues, setExtraValues] = useState<Record<string, string>>(() =>
    Object.fromEntries((config.extras ?? []).map((e) => [e.key, e.initial ?? ''])),
  );
  const [problems, setProblems] = useState<Problems>({});
  // A submit waiting for the answer to `config.confirm`.
  const [pending, setPending] = useState<BulkSubmit | null>(null);

  // "Change SIM Status & Agent Name" asks for no change reason.
  const withReason = !config.agentOrStatus;
  const reasonRequired = withReason && status !== '' && REASON_REQUIRED_STATUSES.includes(status);
  const clear = (key: string) => setProblems((p) => ({ ...p, [key]: undefined }));
  const extras = (config.extras ?? []).filter((e) => !e.pasteOnly || inputType === 'paste');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const found: Problems = {};
    const params: Record<string, unknown> = {};

    if (config.status) {
      if (status) params.status = status;
      else if (!config.agentOrStatus) found.status = 'Select a status';
      if (reasonRequired && !reasonId) found.reasonId = 'Select a change reason';
      if (reasonId && withReason) params.reasonId = reasonId;
    }
    if (config.agent !== 'none') {
      const owner = deepestAgent(chain);
      if (owner) params.ownerUserId = owner;
      else if (config.agent === 'required') found.ownerUserId = 'Select the agent to assign to';
    }
    if (config.agentOrStatus && inputType === 'paste' && !params.status && !params.ownerUserId)
      found.status = config.agentOrStatus;
    for (const extra of extras) {
      const value = (extraValues[extra.key] ?? '').trim();
      if (value) params[extra.key] = value;
      else if (extra.required)
        found[extra.key] =
          extra.source === 'text' ? `${extra.label} is required` : `Select ${extra.label}`;
    }

    let input: BulkSubmit | null = null;
    if (inputType === 'paste') {
      const identifiers = parseIdentifiers(text);
      if (identifiers.length === 0) found.identifiers = `Enter at least one ${config.valueLabel}`;
      else if (identifiers.length > MAX_BULK_VALUES)
        found.identifiers = `At most ${MAX_BULK_VALUES} values per batch`;
      else input = { type: 'paste', identifiers, params };
    } else if (!file) {
      found.file = 'Choose a CSV file';
    } else {
      input = { type: 'file', file, params };
    }

    setProblems(found);
    if (!input || Object.values(found).some(Boolean)) return;
    if (config.confirm) setPending(input);
    else await send(input);
  };

  const send = async (input: BulkSubmit) => {
    try {
      const batch = await submitBulk.mutateAsync(input);
      onBatch(batch);
      notify.success(summary(batch));
      setText('');
      setFile(null);
      setFileKey((k) => k + 1);
    } catch (err) {
      // The API names the field: "params.ownerUserId", "identifiers", "file", ...
      const known = new Set([
        'identifiers',
        'file',
        'ownerUserId',
        'status',
        'reasonId',
        'params',
        ...extras.map((x) => x.key),
      ]);
      const fromServer: Problems = {};
      if (err instanceof ApiError)
        for (const detail of err.details) {
          const key = detail.path.replace(/^params\./, '').split('.')[0] ?? '';
          if (known.has(key)) fromServer[key] = detail.message;
        }
      if (Object.keys(fromServer).length) setProblems(fromServer);
      else notify.error(err);
    } finally {
      setPending(null);
    }
  };

  return (
    <Paper
      variant="outlined"
      component="form"
      noValidate
      onSubmit={(e) => void submit(e)}
      sx={{ p: 2 }}
    >
      {config.notes ? (
        <Box component="ul" aria-label="About this screen" sx={{ mt: 0, mb: 2, pl: 2.5 }}>
          {config.notes.map((note) => (
            <Typography component="li" key={note} color="text.secondary">
              {note}
            </Typography>
          ))}
        </Box>
      ) : null}

      {config.paste ? (
        <Tabs
          value={inputType}
          onChange={(_e, value: InputType) => setInputType(value)}
          sx={{ mb: 2 }}
          aria-label="Input type"
        >
          <Tab value="paste" label="Type - 1" />
          <Tab value="file" label="Type - 2" />
        </Tabs>
      ) : null}

      <Box
        sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' } }}
      >
        {config.status ? (
          <>
            <TextField
              select
              required={!config.agentOrStatus}
              label="Status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as UnitStatus);
                clear('status');
              }}
              error={Boolean(problems.status)}
              helperText={problems.status}
            >
              {config.status.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            {withReason ? (
              <TextField
                select
                required={reasonRequired}
                label="Change Reason"
                value={reasonId}
                onChange={(e) => {
                  setReasonId(e.target.value);
                  clear('reasonId');
                }}
                error={Boolean(problems.reasonId)}
                helperText={
                  problems.reasonId ??
                  (reasons.isError ? 'Could not load the change reasons' : undefined)
                }
              >
                <MenuItem value="">
                  <em>--Select--</em>
                </MenuItem>
                {(reasons.data ?? []).map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                gridColumn: withReason ? 'span 2' : 'span 3',
              }}
            />
          </>
        ) : null}

        {extras.map((extra) => (
          <ExtraField
            key={extra.key}
            extra={extra}
            value={extraValues[extra.key] ?? ''}
            problem={problems[extra.key]}
            onChange={(value) => {
              setExtraValues((v) => ({ ...v, [extra.key]: value }));
              clear(extra.key);
            }}
          />
        ))}

        {config.agent !== 'none' ? (
          <>
            <AgentChainSelect
              value={chain}
              onChange={(next) => {
                setChain(next);
                clear('ownerUserId');
              }}
            />
            <FormHelperText
              error={Boolean(problems.ownerUserId)}
              sx={{ gridColumn: '1 / -1', mt: -1 }}
            >
              {problems.ownerUserId ??
                (config.agent === 'required'
                  ? `The ${config.plural} move to the last agent selected.`
                  : `Optional. Select an agent to also move the ${config.plural}; leave empty to keep their owner.`)}
            </FormHelperText>
          </>
        ) : null}

        {inputType === 'paste' ? (
          <TextField
            multiline
            minRows={6}
            maxRows={14}
            required
            label={config.valueLabel}
            placeholder="One per line, or separated by commas"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              clear('identifiers');
            }}
            error={Boolean(problems.identifiers)}
            helperText={
              problems.identifiers ?? `One per line or comma separated, at most ${MAX_BULK_VALUES}.`
            }
            sx={{ gridColumn: '1 / -1' }}
          />
        ) : (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              {/* The input stays focusable (visually hidden, not `hidden`) so the keyboard can open it. */}
              <Button
                component="label"
                role={undefined}
                tabIndex={-1}
                variant="outlined"
                startIcon={<UploadFileIcon />}
              >
                Choose CSV file
                <Box
                  component="input"
                  key={fileKey}
                  type="file"
                  accept=".csv,text/csv"
                  aria-label="CSV file"
                  sx={VISUALLY_HIDDEN}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setFile(e.target.files?.[0] ?? null);
                    clear('file');
                  }}
                />
              </Button>
              <Typography>{file ? file.name : 'No file chosen'}</Typography>
              {config.template ? (
                <Button
                  component="a"
                  href={addUnitsTemplateUrl()}
                  download
                  startIcon={<DownloadIcon />}
                >
                  Download template
                </Button>
              ) : null}
              {config.sampleHeader ? (
                <Button
                  component="a"
                  href={sampleHref(config.sampleHeader)}
                  download={`${config.operation}-sample.csv`}
                  startIcon={<DownloadIcon />}
                >
                  Download Sample File
                </Button>
              ) : null}
            </Box>
            <FormHelperText error={Boolean(problems.file ?? problems.params)}>
              {problems.file ??
                problems.params ??
                `${config.fileHint} At most ${MAX_BULK_VALUES} rows and 2 MB.`}
            </FormHelperText>
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 2 }}>
        <Button type="submit" variant="contained" disabled={submitBulk.isPending}>
          {submitBulk.isPending ? 'Processing…' : 'Submit'}
        </Button>
      </Box>

      {lastBatch ? (
        <Alert
          severity={severityOf(lastBatch)}
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" onClick={() => onDetails(lastBatch)}>
              View details
            </Button>
          }
        >
          {summary(lastBatch)}
        </Alert>
      ) : null}

      {config.confirm ? (
        <ConfirmDialog
          open={Boolean(pending)}
          title={config.title}
          message={config.confirm}
          confirmLabel="Yes"
          busy={submitBulk.isPending}
          onConfirm={() => {
            if (pending) void send(pending);
          }}
          onClose={() => setPending(null)}
        />
      ) : null}
    </Paper>
  );
}

/** One bulk screen: the form and the batch report. `embedded` leaves out the page header. */
export function BulkScreen({
  config,
  embedded = false,
}: {
  config: BulkConfig;
  embedded?: boolean;
}) {
  const { can } = useAuth();
  const [details, setDetails] = useState<BulkBatchDto | null>(null);
  // The batch of the last submit; replaced by its result once a two-step batch has been sent.
  const [lastBatch, setLastBatch] = useState<BulkBatchDto | null>(null);
  return (
    <Box>
      {embedded ? null : <PageHeader title={config.title} code={config.code} />}
      {/* A report-only permission (PC310) opens the page without the submit form. */}
      {can(config.code) ? (
        <BulkForm
          config={config}
          lastBatch={lastBatch}
          onBatch={setLastBatch}
          onDetails={setDetails}
        />
      ) : null}
      <BatchReport
        config={config}
        onDetails={setDetails}
        onSent={(sent) => setLastBatch((current) => (current?.id === sent.id ? sent : current))}
      />
      <BatchRowsDialog batch={details} onClose={() => setDetails(null)} />
    </Box>
  );
}

/**
 * Bulk Process screens (reference pattern: "Type - 1" paste, "Type - 2" CSV file, then a batch
 * report). One page serves every operation in `BULK_CONFIGS`; the permission depends on it.
 * The operation comes from the address, or from the `operation` prop for a screen that lives
 * elsewhere in the menu (Bulk enrollment).
 */
export function BulkPage({ operation: fixed }: { operation?: BulkOperation }) {
  const { operation } = useParams();
  const config = bulkConfigFor(fixed ?? operation);
  if (!config) return <Alert severity="warning">This bulk process does not exist.</Alert>;
  return (
    <RequirePermission code={[...new Set([config.code, ...config.reportCodes])]}>
      {/* `key` gives each operation its own form state. */}
      <BulkScreen key={config.operation} config={config} />
    </RequirePermission>
  );
}
